import type { UploadFile } from 'antd/es/upload/interface';
import { toast } from 'react-toastify';

import { URL, EMAILS } from '../constants';
import { Event, PublicEvent, Face } from '../types';
import { isEmpty, getUrl } from '../utils';

import { sendEmail } from './api/emails';
import {
  createFace,
  updateFace,
  getFaceByEmail,
  getFaceById,
} from './api/faces';
import { getAttendeeFaceVideos, getAttendeeFacePhotos } from './api/files';
import { uploadFile } from './uploadFileService';

export const checkInPerson = async (props: {
  event: Event | PublicEvent;
  personData: Partial<Face>;
  noEmail?: boolean;
  trackOnSuccess: (isNew: boolean) => void;
  trackOnError: (error: string) => void;
}): Promise<{ face: Face; isNew: boolean }> => {
  const { event, personData, noEmail, trackOnSuccess, trackOnError } = props;
  const email = personData.email ?? '';

  try {
    // check if face exists
    let face = await getFaceByEmail(event.eventId, email, true);
    const isNew = isEmpty(face);

    // if face doesn't exist – create new face, send welcome email & track in GA
    if (isNew) {
      face = await createFace(event.eventId, personData, true, true);

      if (!noEmail) {
        const emailData = {
          recipientEmail: email,
          templateId: event.attendeeReviewData?.isAttendeeReview
            ? EMAILS.CHECK_IN_ATTENDEE_REVIEW
            : event.type === 'brand'
              ? EMAILS.CHECK_IN_BRAND
              : EMAILS.CHECK_IN_VIDEOGRAPHER,
          dynamicTemplateData: {
            event_name: event.name,
            share_url: getUrl(URL.CHECK_IN, event.eventId, undefined, true),
          },
        };

        await sendEmail(emailData);
      }
    }

    trackOnSuccess(isNew);

    return { face, isNew };
  } catch (err: any) {
    trackOnError(err.message);
    throw err;
  }
};

export const updatePersonDetails = async (props: {
  eventId: string;
  elementId: string;
  personData: Partial<Face>;
  trackOnSuccess: () => void;
  trackOnError: () => void;
}) => {
  const { eventId, elementId, personData, trackOnSuccess, trackOnError } =
    props;

  try {
    await updateFace({ eventId, elementId, personData, isAttendee: true });

    trackOnSuccess();

    return { isSuccess: true };
  } catch (error) {
    trackOnError();
    console.error('Error updating person details:', error);
    throw new Error('Failed to update your details. Please try again.');
  }
};

export const updatePersonPhoto = async (props: {
  eventId: string;
  parentElementId: string;
  photo: UploadFile;
  trackOnSuccess: () => void;
  trackOnError: (fileName: string) => void;
}) => {
  const { eventId, parentElementId, photo, trackOnSuccess, trackOnError } =
    props;

  try {
    const fileId = await uploadFile({
      file: photo.originFileObj || photo,
      eventId,
      parentElementId,
      isPublic: true,
    });

    trackOnSuccess();

    return { isSuccess: true, uploadedPhotos: [fileId] };
  } catch (error) {
    trackOnError(photo.name);

    console.error('Error uploading photo:', error);
    throw new Error(
      `<b>${photo.name}</b> upload failed. Please use different photo or try again later.`,
    );
  }
};

export const updatePersonVideos = async (props: {
  eventId: string;
  parentElementId?: string;
  videos: UploadFile[];
  trackOnSuccess: () => void;
  trackOnError: (fileName: string) => void;
  trackOnBatchSuccess: (videoCount: number) => void;
}) => {
  const {
    eventId,
    parentElementId,
    videos,
    trackOnSuccess,
    trackOnError,
    trackOnBatchSuccess,
  } = props;

  let uploadedVideos: string[] = [];
  let videoCount: number = 0;

  await Promise.all(
    videos.map((video) => {
      return new Promise<void>(async (resolve, reject) => {
        return uploadFile({
          file: video.originFileObj || video,
          eventId,
          parentElementId,
          fileKind: 'video-footage',
          isPublic: true,
        })
          .then((fileId) => {
            trackOnSuccess();
            uploadedVideos.push(fileId);
            videoCount++;
            resolve();
          })
          .catch((err) => {
            trackOnError(video.name);
            toast.error(`${video.name} file upload failed.`);
            reject(err);
          });
      });
    }),
  );

  trackOnBatchSuccess(videoCount);

  return { uploadedVideos, videoCount };
};

export const getFaceWithFiles = async (
  eventId: string,
  face: Face,
  refetchFace?: boolean,
) => {
  let attendeeFace = { ...face };

  if (refetchFace)
    attendeeFace = await getFaceByEmail(eventId, face.email, true);

  const videos = await getAttendeeFaceVideos(eventId, face.elementId);
  const photos = await getAttendeeFacePhotos(eventId, face.elementId);

  return { ...attendeeFace, videos, photos };
};

export const getFaceWithFilesById = async (eventId: string, faceId: string) => {
  let attendeeFace = await getFaceById(faceId, true);

  if (!attendeeFace) return null;

  const videos = await getAttendeeFaceVideos(eventId, faceId);
  const photos = await getAttendeeFacePhotos(eventId, faceId);

  return { ...attendeeFace, videos, photos };
};
